import { TAMANHO_MAXIMO_AVATAR, TIPOS_AVATAR_ACEITOS, type ItemHistorico } from '@mymaxx/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Camera,
  ChevronRight,
  LogOut,
  MapPin,
  MessageCircle,
  MessagesSquare,
  Star,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AtendimentoAnterior } from '../componentes/AtendimentoAnterior';
import { ErroApi, authApi, clienteApi } from '../lib/api';
import { internoApi } from '../lib/interno-api';
import { useSessao } from '../lib/sessao';

/** "Todos" não é um ponto: é a ausência de filtro. */
const TODOS = '__todos__';

/**
 * O recorte de tempo da conta do técnico.
 *
 * Abre em "hoje" (decisão de 29/09): o técnico de campo entra aqui entre um serviço e
 * outro para ver como está indo o dia dele, não para reler o mês. O histórico inteiro
 * continua a um toque. Para o cliente comum não faz sentido — ele fala com a MAXX de vez
 * em quando, e abrir no dia mostraria uma tela vazia quase sempre.
 */
type Periodo = 'hoje' | 'tudo';

function ehDeHoje(iso: string): boolean {
  const hoje = new Date();
  const dia = new Date(iso);
  return (
    dia.getDate() === hoje.getDate() &&
    dia.getMonth() === hoje.getMonth() &&
    dia.getFullYear() === hoje.getFullYear()
  );
}

/** Média das notas de uma lista, arredondada a uma casa. `null` quando não há nenhuma. */
function media(notas: (number | null)[]): number | null {
  const validas = notas.filter((n): n is number => n !== null);
  if (validas.length === 0) return null;
  return Math.round((validas.reduce((s, n) => s + n, 0) / validas.length) * 10) / 10;
}

/**
 * A conta do cliente: onde ele abre um atendimento, relê os anteriores e exerce os
 * direitos do titular.
 *
 * O centro da tela é o botão de falar com a gente — é para isso que a pessoa entra. O
 * histórico vem logo abaixo, e cada protocolo abre na própria tela, sem precisar baixar
 * nada: quem quer conferir o que foi combinado não deveria ter de abrir um PDF para isso.
 */
export function Conta() {
  const { cliente, limpar } = useSessao();
  const navegar = useNavigate();
  const [aberto, setAberto] = useState<ItemHistorico | null>(null);
  const [ponto, setPonto] = useState<string>(TODOS);
  const [periodo, setPeriodo] = useState<Periodo>('hoje');

  const historico = useQuery({ queryKey: ['historico'], queryFn: clienteApi.historico });
  // Só o técnico tem setores a escolher; para o cliente comum a lista volta vazia.
  const setoresTecnico = useQuery({
    queryKey: ['setores-tecnico'],
    queryFn: clienteApi.setoresDoTecnico,
    enabled: !!cliente?.tecnico,
  });
  // Só para contar o que não foi lido no atalho das conversas internas; a tela delas é
  // outra. Cliente comum não participa de canal nenhum, então nem se pergunta.
  const canaisInternos = useQuery({
    queryKey: ['interno-canais'],
    queryFn: internoApi.canais,
    enabled: !!cliente?.tecnico,
  });
  const naoLidas = (canaisInternos.data ?? []).reduce((total, c) => total + c.naoLidas, 0);

  /** Só os pontos que aparecem no histórico: filtrar por um sem atendimento não serve. */
  const pontosComAtendimento = useMemo(
    () => [...new Set((historico.data ?? []).map((h) => h.pontoServico).filter(Boolean))],
    [historico.data],
  );

  // O recorte por dia é só do técnico; o cliente comum continua vendo o histórico inteiro.
  const soHoje = !!cliente?.tecnico && periodo === 'hoje';
  const lista = (historico.data ?? [])
    .filter((h) => ponto === TODOS || h.pontoServico === ponto)
    .filter((h) => !soHoje || ehDeHoje(h.iniciadaEm))
    // O pedido em curso não entra na lista do técnico (pedido de 30/09): ele está no botão
    // grande ali em cima, que é por onde se volta para a conversa. Aparecendo nos dois
    // lugares, parecia que havia dois pedidos abertos — e aqui a linha ficava sem duração
    // e sem nota, que é justamente o que o técnico vem conferir.
    .filter((h) => !cliente?.tecnico || h.status === 'encerrada');

  // A nota do dia sai da própria lista; a de sempre vem da sessão, que é a média apurada
  // no banco — o histórico da tela para em 50 itens e faria uma conta errada no "tudo".
  const notaDoPeriodo = soHoje
    ? media(lista.map((h) => h.avaliacaoTecnico))
    : (cliente?.notaMedia ?? null);

  /**
   * As portas de entrada do atendimento.
   *
   * Uma só para o cliente comum e para o técnico que tem um setor só — é a tela de sempre.
   * Com dois ou mais, uma por setor: o técnico de Rotas abre direto onde precisa, sem
   * passar por pergunta nenhuma.
   */
  const setores = setoresTecnico.data ?? [];
  const portas =
    cliente?.tecnico && setores.length > 1
      ? setores.map((s) => ({
          id: s.id,
          destino: `/atendimento?setor=${s.id}`,
          titulo: s.nome,
          ajuda: s.descricao ?? 'Abrir um atendimento neste setor',
        }))
      : [
          {
            id: null,
            destino: '/atendimento',
            titulo: 'Falar com a MAXX',
            ajuda: cliente?.tecnico
              ? 'Abrir um pedido de liberação de sinal'
              : 'Suporte, financeiro ou qualquer dúvida',
          },
        ];

  async function sair() {
    await authApi.logout().catch(() => undefined);
    limpar();
    navegar('/entrar', { replace: true });
  }

  if (aberto) {
    return <AtendimentoAnterior item={aberto} aoVoltar={() => setAberto(null)} />;
  }

  return (
    <main className="mx-auto max-w-2xl space-y-6 px-5 py-8">
      <header className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {/* A foto só existe para quem aparece na conversa interna. Pedir retrato a quem
              só quer falar com a MAXX seria coletar o que não se usa. */}
          {cliente?.tecnico && <MinhaFoto />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-marca-600">MyMAXX</p>
            {/* Sem `truncate`: num celular de 375px, com a foto ao lado, "Wanderson de
                Campo" virava "Wanderson de Cam…". O nome da pessoa cabe em duas linhas. */}
            <h1 className="text-xl leading-tight font-semibold">{cliente?.nome}</h1>
            <p className="text-xs text-neutral-500">
              {cliente?.tecnico ? 'Técnico de liberação' : cliente?.documentoMascarado}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void sair()}
          className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <LogOut size={16} /> Sair
        </button>
      </header>

      {/* A razão de a pessoa estar aqui vem primeiro, e grande.
          Com mais de um setor aberto ao técnico, vira uma porta por setor: escolher antes
          de abrir poupa a conversa de "para onde você quer ir?" (06/10). */}
      {portas.map((porta) => (
        <Link key={porta.id ?? 'unica'} to={porta.destino} className="block">
          <div className="rounded-card flex items-center gap-3 bg-marca-600 p-4 text-white transition hover:bg-marca-700">
            <MessagesSquare size={22} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">{porta.titulo}</p>
              <p className="text-xs opacity-90">{porta.ajuda}</p>
            </div>
            <ChevronRight size={18} className="shrink-0 opacity-80" />
          </div>
        </Link>
      ))}

      {/* A conversa com a equipe, para quem participa de algum canal (hoje, os técnicos).
          Fica abaixo do atendimento de propósito: o cliente é o motivo de ele estar aqui;
          a tratativa interna é o apoio. Antes isto acontecia no Telegram (06/10). */}
      {cliente?.tecnico && (
        <Link to="/conversas" className="block">
          <div className="rounded-card flex items-center gap-3 border border-neutral-200 p-4 transition hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900">
            <MessagesSquare size={20} className="shrink-0 text-marca-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Conversas</p>
              <p className="text-xs text-neutral-500">Falar com a equipe da MAXX</p>
            </div>
            {naoLidas > 0 && (
              <span className="shrink-0 rounded-full bg-marca-600 px-2 py-0.5 text-[11px] font-medium text-white">
                {naoLidas}
              </span>
            )}
            <ChevronRight size={18} className="shrink-0 text-neutral-400" />
          </div>
        </Link>
      )}

      {cliente?.tecnico && (
        <>
          {/* A chave do dia governa a tela inteira do técnico: a nota e a lista. */}
          <div
            role="group"
            aria-label="Período"
            className="rounded-card flex gap-1 border border-neutral-200 p-1 dark:border-neutral-800"
          >
            {(
              [
                ['hoje', 'Hoje'],
                ['tudo', 'Todo o histórico'],
              ] as const
            ).map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                aria-pressed={periodo === valor}
                onClick={() => setPeriodo(valor)}
                className={`flex-1 rounded-lg px-3 py-1.5 text-sm transition ${
                  periodo === valor
                    ? 'bg-marca-600 font-medium text-white'
                    : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>

          <section className="rounded-card flex items-center justify-between gap-3 border border-neutral-200 p-4 dark:border-neutral-800">
            <div>
              <p className="text-sm font-medium">
                {soHoje ? 'Sua nota de hoje' : 'Sua nota com os atendentes'}
              </p>
              <p className="text-xs text-neutral-500">
                {notaDoPeriodo === null
                  ? soHoje
                    ? 'Nenhum pedido seu foi avaliado hoje.'
                    : 'Você ainda não recebeu avaliações.'
                  : soHoje
                    ? 'Média das notas que os atendentes deram aos seus pedidos de hoje.'
                    : 'Média das notas que os atendentes deram aos seus pedidos.'}
              </p>
            </div>
            {notaDoPeriodo !== null && (
              <span className="flex shrink-0 items-center gap-1 text-lg font-semibold">
                <Star size={16} className="fill-amber-400 text-amber-400" />
                {notaDoPeriodo.toFixed(1)}
              </span>
            )}
          </section>
        </>
      )}

      {/* Técnico não é assinante: não há serviço para listar. */}
      {!cliente?.tecnico && !!cliente?.pontosServico.length && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-neutral-500">Seus serviços</h2>
          {cliente.pontosServico.map((p) => (
            <div
              key={p.id}
              className="rounded-card flex items-start gap-3 border border-neutral-200 p-4 dark:border-neutral-800"
            >
              <MapPin className="mt-0.5 shrink-0 text-marca-600" size={18} />
              <div className="min-w-0">
                <p className="text-sm">{p.rotulo}</p>
                {p.status && (
                  <span
                    className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs ${
                      p.status === 'ativo'
                        ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
                        : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                    }`}
                  >
                    {p.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-neutral-500">
            {soHoje ? 'Meus atendimentos de hoje' : 'Meus atendimentos'}
            {historico.data?.length ? ` (${lista.length})` : ''}
          </h2>
          {/* O filtro só aparece quando há o que filtrar: com um ponto só ele seria enfeite. */}
          {pontosComAtendimento.length > 1 && (
            <select
              aria-label="Filtrar por ponto de instalação"
              value={ponto}
              onChange={(e) => setPonto(e.target.value)}
              className="max-w-full rounded-lg border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-950"
            >
              <option value={TODOS}>Todos os pontos</option>
              {pontosComAtendimento.map((p) => (
                <option key={p} value={p!}>
                  {p}
                </option>
              ))}
            </select>
          )}
        </div>

        {historico.isPending && <p className="text-sm text-neutral-500">Carregando…</p>}

        {historico.data?.length === 0 && (
          <div className="rounded-card border border-dashed border-neutral-300 p-6 text-center dark:border-neutral-700">
            <MessageCircle className="mx-auto mb-2 text-neutral-400" size={24} />
            <p className="text-sm text-neutral-500">Você ainda não tem atendimentos por aqui.</p>
          </div>
        )}

        {historico.data?.length !== 0 && lista.length === 0 && (
          <p className="text-sm text-neutral-500">
            {soHoje
              ? 'Nenhum atendimento hoje. Toque em "Todo o histórico" para ver os anteriores.'
              : 'Nenhum atendimento neste ponto.'}
          </p>
        )}

        {lista.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setAberto(item)}
            className="rounded-card flex w-full items-center gap-3 border border-neutral-200 p-4 text-left transition hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{item.protocolo}</span>
                <Situacao status={item.status} />
                {item.avaliacao !== null && (
                  <span className="flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400">
                    {item.avaliacao}
                    <Star size={11} fill="currentColor" />
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm">{item.setor ?? 'Atendimento'}</p>
              <p className="truncate text-xs text-neutral-500">
                {new Date(item.iniciadaEm).toLocaleString('pt-BR')}
                {item.pontoServico ? ` · ${item.pontoServico}` : ''}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-neutral-400" />
          </button>
        ))}
      </section>

      {/* Os direitos da LGPD continuam a dois toques, e saíram da primeira tela (06/10).
          Exportar e, principalmente, "solicitar exclusão" num botão vermelho embaixo do
          histórico é convite ao toque errado — ainda mais no celular. Para o técnico nem
          o link aparece: a conta dele é de trabalho, criada pela empresa. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2">
        {!cliente?.tecnico && (
          <Link to="/conta/meus-dados" className="text-xs text-neutral-500 underline">
            Privacidade e meus dados
          </Link>
        )}
        <Link to="/privacidade" className="text-xs text-neutral-500 underline">
          Ler o aviso de privacidade
        </Link>
      </div>
    </main>
  );
}

function Situacao({ status }: { status: string }) {
  const encerrada = status === 'encerrada';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] ${
        encerrada
          ? 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
          : 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
      }`}
    >
      {encerrada ? 'encerrado' : 'em andamento'}
    </span>
  );
}

/**
 * A foto do técnico: um toque nela troca, e segurar oferece remover.
 *
 * Fica no cabeçalho da conta, e não numa tela de ajustes: é o único retrato do app, e quem
 * entra aqui já está olhando para o próprio nome.
 */
function MinhaFoto() {
  const { cliente, definirCliente } = useSessao();
  const escolher = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);

  async function trocar(arquivo: File) {
    if (arquivo.size > TAMANHO_MAXIMO_AVATAR) return alert('A foto passou de 2 MB');
    if (!TIPOS_AVATAR_ACEITOS.includes(arquivo.type)) {
      return alert('A foto precisa ser JPEG, PNG ou WebP');
    }
    setEnviando(true);
    try {
      definirCliente(await authApi.trocarFoto(arquivo));
    } catch (e) {
      alert(e instanceof ErroApi ? e.mensagem : 'Não consegui trocar a foto');
    } finally {
      setEnviando(false);
      if (escolher.current) escolher.current.value = '';
    }
  }

  const iniciais = (cliente?.nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <>
      <input
        ref={escolher}
        type="file"
        accept={TIPOS_AVATAR_ACEITOS.join(',')}
        className="hidden"
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          if (arquivo) void trocar(arquivo);
        }}
      />
      <button
        type="button"
        disabled={enviando}
        aria-label="Trocar minha foto"
        onClick={() => escolher.current?.click()}
        onContextMenu={(e) => {
          // Segurar o dedo (ou botão direito) oferece tirar a foto — sem um segundo botão
          // ocupando o cabeçalho para algo que se faz uma vez na vida.
          if (!cliente?.avatarUrl) return;
          e.preventDefault();
          if (confirm('Remover sua foto?')) {
            void authApi.removerFoto().then(definirCliente);
          }
        }}
        className="relative shrink-0"
      >
        {cliente?.avatarUrl ? (
          <img
            src={cliente.avatarUrl}
            alt="Minha foto"
            className="size-12 rounded-full object-cover"
          />
        ) : (
          <span className="flex size-12 items-center justify-center rounded-full bg-neutral-200 text-sm font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">
            {iniciais}
          </span>
        )}
        <span className="absolute -right-0.5 -bottom-0.5 flex size-5 items-center justify-center rounded-full bg-marca-600 text-white">
          <Camera size={11} />
        </span>
      </button>
    </>
  );
}
